import { Alert, Badge, Box, Button, Divider, Group, Loader, Stack, Table, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconAlertCircle, IconExclamationCircle, IconSchool } from "@tabler/icons-react";
import { useIsMutating } from "@tanstack/react-query";
import { useEffect } from "react";
import { Section, SectionProvider } from "shared/components/global/Section";
import {
    IpawsAlertFormProvider,
    ipawsAlertFormStateSchemaToMessageDataInput,
    useIpawsAlertFormContext,
} from "shared/components/ipaws/alert/context";
import { useIpawsAlertFormCertificate } from "shared/components/ipaws/alert/query";
import { useIpawsAlertActions } from "shared/components/ipaws/alert/useIpawsAlertActions";
import { formatIpawsChannel } from "shared/components/ipaws/format";
import { MapBase } from "shared/components/map/MapBase";
import { MapFeatureOverlay } from "shared/components/map/MapFeatureOverlay";
import { useCustomer } from "shared/graphql/cust";
import { useFormState } from "shared/hooks/useFormState";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { arr } from "shared/utils/array";
import { fail, runAsync, titlecase, to } from "shared/utils/fns";
import { logger } from "shared/utils/logger";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { IpawsCapMessageInfoWeaHandling, IpawsCertificateType, IpawsEventCode } from "src/gql/graphql";
import { Language } from "src/gql/zod";
import { match, P } from "ts-pattern";

export function useIpawsAlertFormActions() {
    const form = useIpawsAlertFormContext();
    const [step, setStep] = useFormState(form, "meta.step");
    const { data: customer } = useCustomer();

    const createDraft = useGraphqlMutation(muts.createDraft);
    const updateDraft = useGraphqlMutation(muts.updateDraft);

    return {
        createDraft,
        updateDraft,
        openBroadcastModal(params: { onConfirm: (opts: { id: string }) => void }) {
            if (form.validate().hasErrors) {
                logger.warn("Validation failed with: ", form.errors);
                notify.show.warn({ message: "Please fix issues before continuing" });
                return;
            }

            modals.open({
                title: "Review & Send",
                size: "lg",
                children: (
                    <IpawsAlertFormProvider form={form}>
                        <IpawsAlertFormReviewPanel customerId={customer.id} onConfirm={params.onConfirm} />
                    </IpawsAlertFormProvider>
                ),
            });
        },
        nextStep() {
            if (form.validate().hasErrors) {
                logger.warn("Validation failed with: ", form.errors);
                notify.show.warn({ message: "Please fix issues before continuing" });
                return;
            }
            setStep(step + 1);
        },
        prevStep() {
            if (step < 1) setStep(0);
            else setStep(step - 1);
        },
    };
}

const IpawsAlertFormReviewPanel = (props: { customerId: string; onConfirm: (opts: { id: string }) => void }) => {
    const form = useIpawsAlertFormContext();
    const cert = useIpawsAlertFormCertificate();
    const broadcast = useGraphqlMutation(muts.broadcast);
    const broadcastUpdate = useGraphqlMutation(muts.updateAlert);
    const createDraft = useGraphqlMutation(muts.createDraft);
    const updateDraft = useGraphqlMutation(muts.updateDraft);
    const { validate } = useIpawsAlertActions();

    const [mode] = useFormState(form, "meta.mode");
    const [code] = useFormState(form, "eventCode");
    const [areas] = useFormState(form, "areas");
    const [certId] = useFormState(form, "certId");

    const isUpdate = typeof mode == "object" && mode.type == "update";
    const isPending = useIsMutating() > 0;

    const weaHandling = match(code)
        .with(
            P.union(
                IpawsEventCode.Avw,
                IpawsEventCode.Cdw,
                IpawsEventCode.Cem,
                IpawsEventCode.Eqw,
                IpawsEventCode.Evi,
                IpawsEventCode.Frw,
                IpawsEventCode.Hmw,
            ),
            () => IpawsCapMessageInfoWeaHandling.ImminentThreat,
        )
        .with(
            P.union(IpawsEventCode.Blu, IpawsEventCode.Lae, IpawsEventCode.Mep, IpawsEventCode.Toe),
            () => IpawsCapMessageInfoWeaHandling.PublicSafety,
        )
        .with(IpawsEventCode.Cae, () => IpawsCapMessageInfoWeaHandling.ChildAbduction)
        .with(P.union(IpawsEventCode.Dmo, IpawsEventCode.Rwt, IpawsEventCode.Rmt), () => IpawsCapMessageInfoWeaHandling.WeaTest)
        .otherwise(() => null);

    useEffect(() => {
        if (!certId) return fail("Expected certId to be set.");
        if (areas.flatMap(to("features")).length == 0) return;

        validate.mutate({
            certId,
            polygons: areas
                .flatMap(to("features"))
                .map(to("geometry"))
                .map((it) => JSON.stringify(it)),
        });
    }, []);

    function handleBroadcast() {
        runAsync(async () => {
            const payload = ipawsAlertFormStateSchemaToMessageDataInput.parse({
                ...form.getValues(),
                customerId: props.customerId,
            });

            const id = await Promise.resolve(
                match(mode)
                    .with("new", () => createDraft.mutateAsync({ msg: payload }).then(({ draft: { id } }) => id))
                    .with({ draftId: P.string }, ({ draftId }) =>
                        updateDraft.mutateAsync({ id: draftId, msg: payload }).then(({ draft: { id } }) => id),
                    )
                    .with({ alertId: P.string }, ({ alertId }) => alertId)
                    .exhaustive(),
            );

            if (isUpdate) {
                const { alert } = await broadcastUpdate.mutateAsync({ id, msg: payload });
                props.onConfirm({ id: alert.id });
                modals.closeAll();
                return;
            }

            await broadcast.mutateAsync({ id });
            props.onConfirm({ id });
            modals.closeAll();
        });
    }

    const hasPolygonError = areas.flatMap(to("features")).length > 0 && !isPending && !validate.data?.result.success;

    return (
        <Stack>
            <SectionProvider>
                <Section label="Details">
                    <Table variant="vertical" withTableBorder>
                        <Table.Tbody>
                            <Table.Tr>
                                <Table.Th w={120}>Profile</Table.Th>
                                <Table.Td>{cert.data?.ipawsCertificate.name}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Duration</Table.Th>
                                <Table.Td>
                                    {Math.floor(form.values.durationMinutes / 60)} hrs, {form.values.durationMinutes % 60} minutes
                                </Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Channels</Table.Th>
                                <Table.Td>
                                    <Group gap="xs">
                                        {form.values.channels.map((ch) => (
                                            <Badge variant="default" key={ch}>
                                                {formatIpawsChannel(ch)}
                                            </Badge>
                                        ))}
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Event Code</Table.Th>
                                <Table.Td>{form.values.eventCode}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Category</Table.Th>
                                <Table.Td>{form.values.category}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Urgency</Table.Th>
                                <Table.Td>{form.values.urgency}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Severity</Table.Th>
                                <Table.Td>{form.values.severity}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Certainty</Table.Th>
                                <Table.Td>{form.values.certainty}</Table.Td>
                            </Table.Tr>
                        </Table.Tbody>
                    </Table>
                </Section>
                <Section label="Area">
                    {hasPolygonError && (
                        <Alert color="red" title="Invalid Polygon" icon={<IconExclamationCircle />}>
                            You are not permitted to send to the given region. Please check your certificate or update your polygons.
                        </Alert>
                    )}
                    {arr(
                        form.values.areas.map((area, i) => (
                            <Group key={i} gap="xs" align="flex-start" wrap="wrap">
                                {area.features.length > 0 && (
                                    <Box flex={1} pos="relative" style={{ width: "200px", height: "200px" }} tabIndex={-1}>
                                        <MapBase widget id={"ipaws-alert-form-review-" + i} style={{ borderRadius: "4px" }}>
                                            <MapFeatureOverlay features={area.features ?? []} />
                                        </MapBase>
                                    </Box>
                                )}
                                <Table variant="vertical" withTableBorder>
                                    <Table.Tr>
                                        <Table.Th w={120}>Name</Table.Th>
                                        <Table.Td>{area.name}</Table.Td>
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Th>FIPS Codes</Table.Th>
                                        <Table.Td>
                                            <Group gap="xs">
                                                {area.codes.map((code) => (
                                                    <Badge variant="default" key={code}>
                                                        {code}
                                                    </Badge>
                                                ))}
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Th>Polygons</Table.Th>
                                        <Table.Td>{area.features.length}</Table.Td>
                                    </Table.Tr>
                                </Table>
                            </Group>
                        )),
                    )
                        .interleave(<Divider />)
                        .take()}
                </Section>
                <Section label="Message Content">
                    <Stack>
                        <div>
                            <Text fw="bold">Headline</Text>
                            <Text size="sm">{form.values.contents.find((it) => it.language == Language.English)?.headline}</Text>
                        </div>

                        <div>
                            <Text fw="bold">Description</Text>
                            <Text size="sm">{form.values.contents.find((it) => it.language == Language.English)?.description}</Text>
                        </div>

                        {maybe(form.values.contents.find((it) => it.language == Language.English)?.instructions)
                            ?.if((it) => it.trim().length > 0)
                            ?.take((instr) => (
                                <div>
                                    <Text fw="bold">Instruction</Text>
                                    <Text size="sm">{instr}</Text>
                                </div>
                            ))}
                    </Stack>
                </Section>
                {maybe(form.values.contents.find((it) => it.language == Language.English)?.wea)
                    ?.if((it) => it.shortText.trim().length > 0 || it.longText.trim().length > 0)
                    ?.take((wea) => (
                        <Section label="WEA">
                            {maybe(weaHandling)?.take((wh) => (
                                <Table variant="vertical" withTableBorder>
                                    <Table.Tbody>
                                        <Table.Tr>
                                            <Table.Th w={120}>WEA Handling</Table.Th>
                                            <Table.Td>{titlecase(wh.replaceAll("_", " "))}</Table.Td>
                                        </Table.Tr>
                                    </Table.Tbody>
                                </Table>
                            ))}

                            <div>
                                <Text fw="bold">WEA Short Text</Text>
                                <Text size="sm">{wea.shortText}</Text>
                            </div>

                            <div>
                                <Text fw="bold">WEA Long Text</Text>
                                <Text size="sm">{wea.longText}</Text>
                            </div>
                        </Section>
                    ))}

                {maybe(form.values.contents.find((it) => it.language == Language.Spanish))?.take((it) => (
                    <Section label="Message Content (Spanish)">
                        <Stack>
                            <div>
                                <Text fw="bold">Headline</Text>
                                <Text size="sm">{it.headline}</Text>
                            </div>

                            <div>
                                <Text fw="bold">Description</Text>
                                <Text size="sm">{it.description}</Text>
                            </div>

                            {maybe(it.instructions)
                                .if((it) => it.trim().length > 0)
                                ?.take((instr) => (
                                    <div>
                                        <Text fw="bold">Instruction</Text>
                                        <Text size="sm">{instr}</Text>
                                    </div>
                                ))}
                        </Stack>
                    </Section>
                ))}

                {maybe(form.values.contents.find((it) => it.language == Language.Spanish)?.wea)
                    ?.if((it) => it.shortText.trim().length > 0 || it.longText.trim().length > 0)
                    ?.take((wea) => (
                        <Section label="WEA (Spanish)">
                            {maybe(weaHandling)?.take((wh) => (
                                <Table variant="vertical" withTableBorder>
                                    <Table.Tbody>
                                        <Table.Tr>
                                            <Table.Th w={120}>WEA Handling</Table.Th>
                                            <Table.Td>{titlecase(wh.replaceAll("_", " "))}</Table.Td>
                                        </Table.Tr>
                                    </Table.Tbody>
                                </Table>
                            ))}

                            <div>
                                <Text fw="bold">WEA Short Text</Text>
                                <Text size="sm">{wea.shortText}</Text>
                            </div>

                            <div>
                                <Text fw="bold">WEA Long Text</Text>
                                <Text size="sm">{wea.longText}</Text>
                            </div>
                        </Section>
                    ))}
            </SectionProvider>
            <Divider mx="-md" />
            <Group justify="space-between">
                {match(cert.data?.ipawsCertificate.type)
                    .with(IpawsCertificateType.Live, () => (
                        <Alert title="Live Alert" color="red" icon={<IconAlertCircle size={32} />} p="sm" />
                    ))
                    .with(IpawsCertificateType.Test, () => <Alert title="Test Alert" color="blue" icon={<IconSchool size={32} />} p="sm" />)
                    .with(undefined, () => <Loader />)
                    .exhaustive()}
                <Group justify="flex-end">
                    <Button variant="default" onClick={modals.closeAll}>
                        Cancel
                    </Button>
                    <Button loading={isPending} onClick={handleBroadcast} disabled={hasPolygonError}>
                        Broadcast
                    </Button>
                </Group>
            </Group>
        </Stack>
    );
};

const muts = {
    createDraft: graphql(`
        mutation IpawsAlertCreateDraft($msg: IpawsMessageDataInput!) {
            draft: createDraftIpawsAlert(message: $msg) {
                id
            }
        }
    `),
    updateDraft: graphql(`
        mutation IpawsAlertUpdateDraft($id: UUID!, $msg: IpawsMessageDataInput!) {
            draft: updateDraftIpawsAlert(id: $id, message: $msg) {
                id
            }
        }
    `),
    updateAlert: graphql(`
        mutation IpawsAlertUpdateAlert($id: UUID!, $msg: IpawsMessageDataInput!) {
            alert: updateIpawsAlert(id: $id, update: $msg) {
                id
            }
        }
    `),
    broadcast: graphql(`
        mutation IpawsAlertBroadcast($id: UUID!) {
            broadcastIpawsAlert(id: $id) {
                capMessage {
                    identifier
                    status
                }
            }
        }
    `),
};
